import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { imageSrc } from "@/lib/site";
import type { Season } from "@/hooks/useSeason";

function HeroFade({ season }: { season: Season }) {
  const photos = [...new Set([season.image, ...season.featured.map((p) => p.image)].filter(Boolean) as string[])].slice(0, 4);
  const [i, setI] = useState(0);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (photos.length < 2 || window.matchMedia("(prefers-reduced-motion: reduce)").matches) { return; }
    const t = setInterval(() => setI((v) => (v + 1) % photos.length), 5500);
    return () => clearInterval(t);
  }, [photos.length]);
  return (
    <a href="#right-now" className="group relative block aspect-[4/3] overflow-hidden rounded-2xl bg-[#F5F5F4] fade-up lg:aspect-[5/4]" style={{ animationDelay: "100ms" }} data-testid="season-hero-photo">
      {photos.map((src, n) => (n === 0 || ready) && (
        <img key={src} src={imageSrc(src)} alt={n === 0 ? season.featured[0]?.title || season.name : ""} decoding="async" onLoad={n === 0 ? () => setTimeout(() => setReady(true), 1500) : undefined}
          className={`absolute inset-0 h-full w-full object-cover transition-[opacity,transform] duration-[1400ms] ease-in-out group-hover:scale-[1.03] ${n === i ? "opacity-100" : "opacity-0"}`}
          data-testid={n === 0 ? "season-hero-image" : `season-hero-image-${n}`} />
      ))}
      {photos.length > 1 && (
        <span className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-1.5" data-testid="season-hero-dots">
          {photos.map((src, n) => <span key={src} className={`h-1.5 rounded-full bg-white shadow transition-[width,opacity] duration-500 ${n === i ? "w-5 opacity-100" : "w-1.5 opacity-60"}`} />)}
        </span>
      )}
    </a>
  );
}

export function SeasonHero({ season }: { season: Season }) {
  return (
    <section className="mx-auto max-w-7xl px-5 pb-8 pt-10 sm:px-8 lg:pt-16" data-testid="season-hero">
      <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
        <div className="fade-up">
          <p className="shop-eyebrow" data-testid="season-kicker">DIY gift ideas + printable gift kits</p>
          <h1 className="shop-h1 mt-5 text-4xl sm:text-5xl lg:text-6xl" data-testid="home-headline">Handmade gifts people actually keep.</h1>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-[#78716C] sm:text-lg">
            Easy, step-by-step DIY gift ideas for everyone on your list, with real costs and times. Short on time? Grab a print-at-home kit that does the hard part for you.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/gift-finder" className="btn-solid" data-testid="home-finder-btn"><Sparkles className="h-4 w-4" />Help me find a gift</Link>
            <a href="#find-a-gift" className="btn-line" data-testid="home-ideas-btn">Browse by category <ArrowRight className="h-4 w-4" /></a>
          </div>
        </div>
        <HeroFade season={season} />
      </div>
    </section>
  );
}

const STEPS = [
  { n: "1", title: "Pick who it's for", text: "Start with the person, the occasion or your budget.", to: "#find-a-gift", cta: "Browse ideas" },
  { n: "2", title: "Make it by hand", text: "Every idea lists materials, time, cost and the steps in order.", to: "/blog", cta: "See an idea" },
  { n: "3", title: "Or print a kit", text: "Instant-download printables for when you want it done tonight.", to: "/shop", cta: "Shop printables" },
];

export function HowItWorks() {
  return (
    <section className="mx-auto max-w-7xl px-5 pt-12 sm:px-8" data-testid="home-how-it-works">
      <div className="grid gap-px overflow-hidden rounded-2xl border border-[#E7E5E4] bg-[#E7E5E4] sm:grid-cols-3">
        {STEPS.map((s) => {
          const inner = (
            <>
              <span className="font-['Plus_Jakarta_Sans'] text-3xl font-bold text-[#B85C38]">{s.n}</span>
              <p className="shop-h2 mt-2 text-lg">{s.title}</p>
              <p className="mt-1 text-sm text-[#78716C]">{s.text}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#1C1917]">{s.cta} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" /></span>
            </>
          );
          const cls = "group block bg-white p-6 transition-colors hover:bg-[#FAFAF9] sm:p-7";
          return s.to.startsWith("#")
            ? <a key={s.n} href={s.to} className={cls} data-testid={`how-step-${s.n}`}>{inner}</a>
            : <Link key={s.n} to={s.to} className={cls} data-testid={`how-step-${s.n}`}>{inner}</Link>;
        })}
      </div>
    </section>
  );
}
